import { useState, useRef, useEffect, useCallback } from 'react'
import { hidePurchases } from '../lib/native'
import { api } from '../lib/api'
import { getSageContext, subscribeSage, consumePendingAsk } from '../lib/sagebus'
import type { ChatMessage, ChatThread, A2UIBlock, ToolReceipt } from '../lib/types'

/**
 * SageDock — the embedded right-rail AI companion.
 * Not a floating bubble: it lives in the page layout, collapsible, with thread
 * history, voice in/out, quick-starts, an animated avatar, and MCP action
 * receipts (Sage adds relatives, plans meet-ups, shares trees by itself).
 */

declare global {
  interface Window {
    webkitSpeechRecognition?: new () => SpeechRecognitionLike
    SpeechRecognition?: new () => SpeechRecognitionLike
  }
}
interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
  start: () => void
  stop: () => void
}

const QUICK_STARTS = [
  { icon: '🌱', text: 'Add my grandmother — she was born in 1938' },
  { icon: '🔍', text: 'What is missing from this tree?' },
  { icon: '📅', text: 'Plan a family reunion next month' },
  { icon: '🗺️', text: 'How do I research immigration records?' },
]

function SageAvatar({ thinking, speaking }: { thinking: boolean; speaking: boolean }) {
  return (
    <div className={`relative w-9 h-9 flex-shrink-0 ${speaking ? 'animate-pulse' : ''}`}>
      <svg viewBox="0 0 40 40" className="w-full h-full">
        <defs>
          <radialGradient id="sageGlow" cx="50%" cy="40%">
            <stop offset="0%" stopColor="#e8bc6a" />
            <stop offset="100%" stopColor="#7c3aed" />
          </radialGradient>
        </defs>
        <circle cx="20" cy="20" r="18" fill="url(#sageGlow)" opacity="0.25" className={thinking ? 'animate-ping' : ''} style={{ animationDuration: '2s' }} />
        <circle cx="20" cy="20" r="15" fill="#161129" stroke="#e8bc6a" strokeWidth="1.5" />
        {/* constellation face */}
        <circle cx="14.5" cy="17" r="1.8" fill="#e8bc6a" className={thinking ? 'animate-pulse' : ''} />
        <circle cx="25.5" cy="17" r="1.8" fill="#e8bc6a" className={thinking ? 'animate-pulse' : ''} />
        <path d={speaking ? 'M14 25 Q20 30 26 25' : 'M14 25.5 Q20 28 26 25.5'} stroke="#a78bfa" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        <circle cx="20" cy="8.5" r="1.2" fill="#a78bfa" />
        <line x1="20" y1="9.5" x2="20" y2="12" stroke="#a78bfa" strokeWidth="0.8" opacity="0.7" />
      </svg>
    </div>
  )
}

/** Sage emits this URL, and its context contains collaborator-writable tree text —
 *  so treat it as untrusted: only http(s) survives, everything else (javascript:,
 *  data:, blob:) is dropped. React does NOT block javascript: hrefs (D-105). */
function safeHttpUrl(u?: string): string | undefined {
  if (!u) return undefined
  try {
    const parsed = new URL(u, 'https://family-ancestry.istayintech.com')
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : undefined
  } catch { return undefined }
}

export default function SageDock({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [threadId, setThreadId] = useState<string | null>(null)
  const [showThreads, setShowThreads] = useState(false)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [listening, setListening] = useState(false)
  const [voiceOut, setVoiceOut] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  const [ctxTick, setCtxTick] = useState(0)
  const [appliedFixes, setAppliedFixes] = useState<Set<string>>(new Set())
  const [fixResults, setFixResults] = useState<Map<string, string>>(new Map())
  const [selectedFixes, setSelectedFixes] = useState<Set<string>>(new Set())
  const [credits, setCredits] = useState<{ balance: number; included_limit: number; included_used: number; tier: string } | null>(null)
  const fixBlocks = useRef<Map<string, Extract<A2UIBlock, { type: 'proposed_action' }>>>(new Map())
  const scrollRef = useRef<HTMLDivElement>(null)
  const recRef = useRef<SpeechRecognitionLike | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const sage = getSageContext()

  useEffect(() => subscribeSage(() => setCtxTick(t => t + 1)), [])
  void ctxTick

  // consume a queued “ask Sage …” from another view (fires after the dock opens)
  useEffect(() => {
    if (!open || busy) return
    const pending = consumePendingAsk()
    if (pending) send(pending)
  }, [open, ctxTick]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      api.listChats().then(setThreads).catch(() => {})
      api.getCredits().then(c => setCredits({ balance: c.balance, included_limit: c.included_limit, included_used: c.included_used, tier: c.tier })).catch(() => {})
    }
  }, [open])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, busy])

  // persist thread (debounced)
  const persist = useCallback((msgs: ChatMessage[], tid: string | null) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      if (msgs.length === 0) return
      const title = msgs[0]?.content.slice(0, 48) || 'New conversation'
      try {
        const res = await api.saveChat({ id: tid || undefined, tree_id: getSageContext().treeId, title, messages: msgs })
        if (!tid) {
          setThreadId(res.id)
          api.listChats().then(setThreads).catch(() => {})
        }
      } catch { /* offline tolerable */ }
    }, 600)
  }, [])

  const speak = useCallback((text: string) => {
    if (!voiceOut || !('speechSynthesis' in window)) return
    window.speechSynthesis.cancel()
    const u = new SpeechSynthesisUtterance(text)
    u.rate = 1.02
    u.pitch = 0.95
    u.onstart = () => setSpeaking(true)
    u.onend = () => setSpeaking(false)
    window.speechSynthesis.speak(u)
  }, [voiceOut])

  const send = useCallback(async (text?: string) => {
    const content = (text ?? input).trim()
    if (!content || busy) return
    setInput('')
    const base: ChatMessage[] = [...messages, { role: 'user', content }]
    setMessages(base)
    setBusy(true)
    try {
      const res = await api.agentChat(
        base.map(m => ({ role: m.role, content: m.content })),
        getSageContext().treeId,
      )
      const next: ChatMessage[] = [...base, { role: 'assistant', content: res.reply, ui: res.ui, actions: res.actions }]
      setMessages(next)
      localStorage.setItem('fa_sage_used', '1')
      persist(next, threadId)
      speak(res.reply)
      if (res.actions?.some(a => a.ok)) getSageContext().refresh?.()
      if (res.credit) {
        setCredits(prev => prev
          ? { ...prev, balance: res.credit!.balance, included_used: res.credit!.used ? prev.included_used : prev.included_used + 1 }
          : prev)
      }
    } catch (e) {
      const msg = e instanceof Error && e.message.includes('402')
        ? (hidePurchases()
            ? 'Your Sage allowance is spent for now — it renews monthly.'
            : 'Your Sage credits are spent for now. Add more from Profile → Sage Credits, or wait for your monthly allowance to renew.')
        : 'The archive is quiet right now — try again in a moment.'
      const next: ChatMessage[] = [...base, { role: 'assistant', content: msg }]
      setMessages(next)
      if (e instanceof Error && e.message.includes('402')) api.getCredits().then(c => setCredits({ balance: c.balance, included_limit: c.included_limit, included_used: c.included_used, tier: c.tier })).catch(() => {})
    } finally { setBusy(false) }
  }, [input, busy, messages, threadId, persist, speak])

  const toggleMic = useCallback(() => {
    if (listening) { recRef.current?.stop(); return }
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Ctor) { alert('Voice input is not supported in this browser'); return }
    const rec = new Ctor()
    rec.lang = 'en-US'
    rec.continuous = false
    rec.interimResults = true
    let final = ''
    rec.onresult = e => {
      const t = Array.from({ length: e.results.length }, (_, i) => e.results[i][0].transcript).join('')
      final = t
      setInput(t)
    }
    rec.onend = () => {
      setListening(false)
      if (final.trim()) send(final)
    }
    rec.onerror = () => setListening(false)
    recRef.current = rec
    setListening(true)
    rec.start()
  }, [listening, send])

  const newChat = () => {
    setMessages([])
    setThreadId(null)
    setShowThreads(false)
  }

  const openThread = async (t: ChatThread) => {
    try {
      const full = await api.getChat(t.id)
      setMessages(JSON.parse(full.messages || '[]'))
      setThreadId(t.id)
      setShowThreads(false)
    } catch { /* ignore */ }
  }

  const removeThread = async (t: ChatThread, e: React.MouseEvent) => {
    e.stopPropagation()
    await api.deleteChat(t.id)
    if (t.id === threadId) newChat()
    setThreads(prev => prev.filter(x => x.id !== t.id))
  }

  const applyOne = async (block: Extract<A2UIBlock, { type: 'proposed_action' }>, key: string) => {
    const treeId = getSageContext().treeId
    if (!treeId) return false
    try {
      const receipt = await api.executeTool(treeId, block.tool, block.args)
      setAppliedFixes(prev => new Set(prev).add(key))
      setFixResults(prev => new Map(prev).set(key, receipt.ok ? receipt.summary : `⚠ ${receipt.error}`))
      return receipt.ok
    } catch { return false }
  }

  const applyFix = async (block: Extract<A2UIBlock, { type: 'proposed_action' }>, key: string) => {
    const ok = await applyOne(block, key)
    setSelectedFixes(prev => { const n = new Set(prev); n.delete(key); return n })
    if (ok) getSageContext().refresh?.()
  }

  // batch-apply every selected proposed_action, in order
  const applySelected = async () => {
    let anyOk = false
    for (const key of selectedFixes) {
      const block = fixBlocks.current.get(key)
      if (block && !appliedFixes.has(key)) anyOk = (await applyOne(block, key)) || anyOk
    }
    setSelectedFixes(new Set())
    if (anyOk) getSageContext().refresh?.()
  }

  const toggleFix = (key: string) => setSelectedFixes(prev => {
    const n = new Set(prev)
    n.has(key) ? n.delete(key) : n.add(key)
    return n
  })

  const renderBlock = (block: A2UIBlock, key: string) => {
    if (block.type === 'card') {
      return (
        <div key={key} className="mt-2 bg-void-700/60 border border-gold-500/20 rounded-xl p-3">
          <p className="text-xs font-semibold text-gold-300 mb-1">{block.icon ? `${block.icon} ` : ''}{block.title}</p>
          <p className="text-xs text-slate-300 leading-relaxed">{block.body}</p>
        </div>
      )
    }
    if (block.type === 'suggestions') {
      return (
        <div key={key} className="mt-2 flex flex-wrap gap-1.5">
          {block.items.slice(0, 4).map((s, i) => (
            <button key={i} onClick={() => send(s)}
              className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-gold-500/40 hover:text-gold-300 transition-colors">
              {s}
            </button>
          ))}
        </div>
      )
    }
    if (block.type === 'plan') {
      return (
        <div key={key} className="mt-2 bg-rootv-600/10 border border-rootv-500/25 rounded-xl p-3">
          <p className="text-xs font-semibold text-rootv-400 mb-1.5">🗺️ {block.title}</p>
          <ol className="space-y-1">
            {block.steps.slice(0, 6).map((s, i) => (
              <li key={i} className="text-xs text-slate-300 flex gap-2">
                <span className="text-rootv-400 font-mono flex-shrink-0">{i + 1}.</span>{s}
              </li>
            ))}
          </ol>
        </div>
      )
    }
    if (block.type === 'record_hint') {
      return (
        <div key={key} className="mt-2 bg-void-700/60 border border-white/10 rounded-xl p-3">
          <p className="text-xs font-semibold text-slate-100">📇 {block.title}</p>
          <p className="text-[10px] text-gold-400/80 mb-1">{block.source}</p>
          <p className="text-xs text-slate-300 leading-relaxed">{block.body}</p>
          {safeHttpUrl(block.url) && <a href={safeHttpUrl(block.url)} target="_blank" rel="noreferrer noopener" className="text-[11px] text-rootv-400 underline mt-1 inline-block">Open source ↗</a>}
        </div>
      )
    }
    if (block.type === 'proposed_action') {
      fixBlocks.current.set(key, block)
      const done = appliedFixes.has(key)
      const selected = selectedFixes.has(key)
      if (done) {
        return (
          <div key={key} className="mt-2 w-full text-xs px-3 py-2.5 rounded-xl border bg-emerald-500/10 border-emerald-500/30 text-emerald-300">
            ✓ {fixResults.get(key) || 'Applied'}
          </div>
        )
      }
      return (
        <div key={key} className={`mt-2 w-full flex items-center gap-2 text-xs px-2.5 py-2 rounded-xl border transition-colors ${
          selected ? 'bg-gold-500/20 border-gold-500/50' : 'bg-gold-500/10 border-gold-500/30'}`}>
          <button onClick={() => toggleFix(key)} title="Select to apply with others"
            className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center ${selected ? 'bg-gold-400 border-gold-400 text-void-950' : 'border-gold-500/50'}`}>
            {selected ? '✓' : ''}
          </button>
          <button onClick={() => applyFix(block, key)} className="flex-1 text-start text-gold-200 hover:text-gold-100">
            {block.label}
          </button>
          <button onClick={() => applyFix(block, key)} className="text-gold-300 hover:text-gold-100 flex-shrink-0" title="Apply now">⊕</button>
        </div>
      )
    }
    return null
  }

  const renderReceipts = (actions?: ToolReceipt[]) => {
    if (!actions?.length) return null
    return (
      <div className="mt-2 space-y-1">
        {actions.map((a, i) => (
          <div key={i} className={`text-[11px] px-2.5 py-1.5 rounded-lg border flex items-start gap-1.5 ${
            a.ok ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300' : 'bg-red-500/10 border-red-500/25 text-red-300'
          }`}>
            <span>{a.ok ? '⚡' : '⚠'}</span>
            <span>{a.ok ? a.summary : `${a.tool}: ${a.error}`}</span>
          </div>
        ))}
      </div>
    )
  }

  if (!open) return null

  return (
    <aside className="sage-dock flex flex-col bg-void-900/95 backdrop-blur-xl border-l border-white/[0.08]">
      {/* header */}
      <div className="px-3.5 py-3 border-b border-white/[0.08] flex items-center gap-2.5">
        <SageAvatar thinking={busy} speaking={speaking} />
        <div className="min-w-0 flex-1">
          <p className="font-display font-semibold text-sm text-slate-100">Sage</p>
          <p className="text-[10px] text-slate-500 truncate">
            {sage.treeName ? `watching “${sage.treeName}”` : 'no tree open — read-only chat'}
          </p>
        </div>
        <button onClick={() => setVoiceOut(v => { if (v) window.speechSynthesis?.cancel(); return !v })}
          title={voiceOut ? 'Voice replies on' : 'Voice replies off'}
          className={`w-7 h-7 rounded-lg text-sm flex items-center justify-center ${voiceOut ? 'bg-gold-500/20 text-gold-300' : 'text-slate-500 hover:text-slate-300'}`}>
          {voiceOut ? '🔊' : '🔇'}
        </button>
        <button onClick={() => setShowThreads(s => !s)} title="History"
          className={`w-7 h-7 rounded-lg text-sm flex items-center justify-center ${showThreads ? 'bg-white/10 text-gold-300' : 'text-slate-500 hover:text-slate-300'}`}>
          🕐
        </button>
        <button onClick={newChat} title="New chat" className="w-7 h-7 rounded-lg text-sm flex items-center justify-center text-slate-500 hover:text-slate-300">＋</button>
        <button onClick={onClose} title="Hide Sage" className="w-7 h-7 rounded-lg text-sm flex items-center justify-center text-slate-500 hover:text-slate-300">⇥</button>
      </div>

      {/* usage bar: monthly allowance first, then credits (v0.9.0) */}
      {credits && (
        <a href="/profile" title="Manage Sage credits in Profile"
          className="block px-3.5 py-1.5 border-b border-white/[0.06] hover:bg-white/[0.03] transition-colors">
          <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
            <span>
              {credits.included_limit > 0
                ? `Plan allowance ${Math.min(credits.included_used, credits.included_limit)}/${credits.included_limit} this month`
                : 'No plan allowance on this tier'}
            </span>
            <span className={credits.balance <= 10 ? 'text-amber-400' : 'text-slate-400'}>
              {credits.balance} credits
            </span>
          </div>
          <div className="h-1 rounded-full bg-white/[0.07] overflow-hidden">
            {credits.included_limit > 0 ? (
              <div className={`h-full ${credits.included_used >= credits.included_limit ? 'bg-amber-500/70' : 'bg-gradient-to-r from-emerald-500/70 to-gold-500/70'}`}
                style={{ width: `${Math.min(100, (credits.included_used / credits.included_limit) * 100)}%` }} />
            ) : (
              <div className={`h-full ${credits.balance <= 10 ? 'bg-amber-500/70' : 'bg-gold-500/60'}`}
                style={{ width: `${Math.min(100, credits.balance)}%` }} />
            )}
          </div>
        </a>
      )}

      {/* thread history overlay */}
      {showThreads ? (
        <div className="flex-1 overflow-y-auto p-2.5 space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-slate-600 px-1.5 pb-1">Conversations</p>
          {threads.map(t => (
            <button key={t.id} onClick={() => openThread(t)}
              className={`w-full text-start px-2.5 py-2 rounded-xl border transition-colors group ${
                t.id === threadId ? 'bg-gold-500/10 border-gold-500/30' : 'bg-white/[0.03] border-white/[0.06] hover:border-white/15'}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-slate-200 truncate flex-1">{t.title}</p>
                <span onClick={e => removeThread(t, e)} className="text-slate-600 hover:text-red-400 text-xs opacity-0 group-hover:opacity-100">✕</span>
              </div>
              <p className="text-[10px] text-slate-600">{t.updated_at.slice(0, 16).replace('T', ' ')}</p>
            </button>
          ))}
          {threads.length === 0 && <p className="text-xs text-slate-500 text-center py-8">No saved conversations yet.</p>}
        </div>
      ) : (
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
          {messages.length === 0 && (
            <div className="pt-6">
              <p className="text-sm text-slate-300 text-center mb-1">I can <span className="text-gold-300">do</span> things, not just answer.</p>
              <p className="text-[11px] text-slate-500 text-center mb-4">Add relatives, record resting places, plan meet-ups, share trees — by chat or voice.</p>
              <div className="flex flex-col gap-1.5">
                {QUICK_STARTS.map(q => (
                  <button key={q.text} onClick={() => send(q.text)}
                    className="text-start text-xs px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-slate-300 hover:border-gold-500/40 transition-colors">
                    <span className="me-1.5">{q.icon}</span>{q.text}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, mi) => (
            <div key={mi} className={m.role === 'user' ? 'flex justify-end' : 'flex gap-2'}>
              {m.role === 'assistant' && <div className="pt-0.5"><SageAvatar thinking={false} speaking={false} /></div>}
              <div className={`max-w-[88%] rounded-2xl px-3 py-2.5 text-sm ${
                m.role === 'user' ? 'bg-gold-500/15 border border-gold-500/20 text-slate-100'
                  : 'bg-white/[0.05] border border-white/[0.07] text-slate-200'}`}>
                <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                {renderReceipts(m.actions)}
                {m.ui?.map((b, bi) => renderBlock(b, `${mi}-${bi}`))}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex gap-2 items-center">
              <SageAvatar thinking speaking={false} />
              <div className="flex gap-1.5 px-2">
                <span className="typing-dot w-2 h-2 rounded-full bg-gold-400" />
                <span className="typing-dot w-2 h-2 rounded-full bg-gold-400" />
                <span className="typing-dot w-2 h-2 rounded-full bg-gold-400" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* batch-apply bar for multi-selected suggestions */}
      {selectedFixes.size >= 2 && (
        <div className="px-3 py-2 border-t border-gold-500/20 bg-gold-500/[0.06] flex items-center justify-between">
          <span className="text-xs text-gold-200">{selectedFixes.size} actions selected</span>
          <div className="flex gap-2">
            <button onClick={() => setSelectedFixes(new Set())} className="text-xs text-slate-400 hover:text-slate-200">Clear</button>
            <button onClick={applySelected} className="btn-gold !px-3 !py-1 text-xs">Apply {selectedFixes.size} together</button>
          </div>
        </div>
      )}

      {/* composer */}
      <form onSubmit={e => { e.preventDefault(); send() }} className="p-2.5 border-t border-white/[0.08] flex gap-1.5 items-center">
        <button type="button" onClick={toggleMic}
          className={`w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0 border transition-colors ${
            listening ? 'bg-red-500/20 border-red-500/50 text-red-300 animate-pulse' : 'bg-white/[0.05] border-white/10 text-slate-400 hover:text-gold-300'}`}
          title={listening ? 'Listening… tap to stop' : 'Speak to Sage'}>
          🎙
        </button>
        <input value={input} onChange={e => setInput(e.target.value)}
          placeholder={listening ? 'Listening…' : 'Ask or command Sage…'}
          className="input-dark !py-2 text-sm flex-1 min-w-0" />
        <button type="submit" disabled={busy || !input.trim()} className="btn-gold !px-3.5 !py-2 text-sm flex-shrink-0">↑</button>
      </form>
    </aside>
  )
}
